import React, {PropsWithChildren, useContext, useEffect, useState} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {XpertKnowledgeSectionDto} from '../../services/XpertKnowledgeSection/type'
import {XpertKnowledgeTutorialDto} from '../../services/XpertKnowledgeTutorial/type'
import {XpertKnowledgePublicTutorialService} from '../../services/XpertKnowledgePublicTutorial'
import type {AxiosError} from 'axios'

interface XpertKnowledgePublicTutorialContext {
  xpertKnowledgeId?: string
  publicXpertKnowledgeTutorial?: XpertKnowledgeTutorialDto
  xpertKnowledgeSections: XpertKnowledgeSectionDto[]
  submitPublicRating: (xpertKnowledgeId: string, rating: number) => Promise<void>
}

const Context = React.createContext<XpertKnowledgePublicTutorialContext>(null as any)

interface XpertKnowledgePublicTutorialContextProviderProps {}

export const XpertKnowledgePublicTutorialContextProvider: React.FC<PropsWithChildren<XpertKnowledgePublicTutorialContextProviderProps>> = ({
  children,
}) => {
  const {xpertKnowledgeId} = useParams<{xpertKnowledgeId: string}>()
  const [publicXpertKnowledgeTutorial, setPublicXpertKnowledgeTutorial] = useState<XpertKnowledgeTutorialDto | undefined>(undefined)
  const [xpertKnowledgeSections, setXpertKnowledgeSections] = useState<XpertKnowledgeSectionDto[]>([])
  const navigate = useNavigate()

  const getUserUuidFromIndexedDB = async (): Promise<string | undefined> => {
    if (!('indexedDB' in window)) return undefined
    return new Promise(async (resolve) => {
      const dbs = await window.indexedDB.databases()
      if (!dbs.map((db) => db.name).includes('XpertKnowledgeDB')) return resolve(undefined)

      const request = indexedDB.open('XpertKnowledgeDB', 1)
      request.onsuccess = function () {
        const db = request.result
        if (!db.objectStoreNames.contains('user')) {
          db.close()
          deleteXpertKnowledgeDB()
          return resolve(undefined)
        }
        const tx = db.transaction('user', 'readonly')
        const store = tx.objectStore('user')
        const getReq = store.get('user_uuid')
        getReq.onsuccess = function () {
          resolve(getReq.result)
          db.close()
        }
        getReq.onerror = function () {
          db.close()
          deleteXpertKnowledgeDB()
          resolve(undefined)
        }
      }
      request.onerror = function () {
        deleteXpertKnowledgeDB()
        resolve(undefined)
      }
    })
  }

  const setUserUuidToIndexedDB = async (uuid: string): Promise<void> => {
    if (!('indexedDB' in window)) return
    return new Promise((resolve) => {
      const request = indexedDB.open('XpertKnowledgeDB', 1)
      request.onupgradeneeded = function (event) {
        const db = request.result
        if (!db.objectStoreNames.contains('user')) {
          db.createObjectStore('user')
        }
      }
      request.onsuccess = function () {
        const db = request.result
        if (!db.objectStoreNames.contains('user')) {
          db.close()
          return resolve()
        }
        const tx = db.transaction('user', 'readwrite')
        const store = tx.objectStore('user')
        store.put(uuid, 'user_uuid')
        tx.oncomplete = function () {
          db.close()
          resolve()
        }
        tx.onerror = function () {
          db.close()
          resolve()
        }
      }
      request.onerror = function () {
        resolve()
      }
    })
  }

  const deleteXpertKnowledgeDB = async (): Promise<void> => {
    if (!('indexedDB' in window)) return
    return new Promise((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase('XpertKnowledgeDB')
      deleteRequest.onsuccess = function () {
        resolve()
      }
      deleteRequest.onerror = function () {
        reject(deleteRequest.error)
      }
      deleteRequest.onblocked = function () {
        resolve()
      }
    })
  }

  const getOrCreateUuid = async (): Promise<string> => {
    let uuid
    uuid = (await getUserUuidFromIndexedDB()) ?? null
    if (uuid) {
      return uuid
    }

    uuid = crypto.randomUUID()
    await setUserUuidToIndexedDB(uuid)
    return uuid
  }

  useEffect(() => {
    if (xpertKnowledgeId) {
      fetchXpertKnowledgeTutorial(xpertKnowledgeId)
    }
  }, [xpertKnowledgeId])

  const fetchXpertKnowledgeTutorial = async (xpertKnowledgeId: string) => {
    try {
      const userUuid = await getOrCreateUuid()
      const tutorial = await XpertKnowledgePublicTutorialService.getPublicById(xpertKnowledgeId, userUuid)
      setPublicXpertKnowledgeTutorial(tutorial)
      tutorial.sections.map((section) => {
        section.subSections = section.subSections?.sort((a, b) => a.position - b.position)
        return section
      })
      setXpertKnowledgeSections(tutorial.sections.sort((a, b) => a.position - b.position))
    } catch (error) {
      console.error('Error fetching Xpert Knowledge Tutorial:', error)
      if ((error as AxiosError).status === 404) {
        navigate('/xpert-knowledge/tutorial/not-found')
      } else {
        navigate('/xpert-knowledge/error')
      }
    }
  }

  const submitPublicRating = async (xpertKnowledgeId: string, rating: number) => {
    const userUuid = await getOrCreateUuid()
    const xkTutorial = await XpertKnowledgePublicTutorialService.submitPublicRating({xpertKnowledgeId, rating, guid: userUuid})
    setPublicXpertKnowledgeTutorial(xkTutorial)
  }

  return (
    <Context.Provider
      value={{
        xpertKnowledgeId,
        publicXpertKnowledgeTutorial,
        xpertKnowledgeSections,
        submitPublicRating,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useXpertKnowledgePublicTutorialContext = () => useContext(Context)
